<template>
  <div
    class="flex min-h-[4.25rem] items-center rounded-xl border border-gray-100 bg-gray-50 px-4 py-3"
    data-testid="doctor-invite-card"
  >
    <div class="flex w-full items-center gap-3 min-w-0">
      <span
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-100 bg-white text-lg text-gray-500"
      >
        <SVGIcon :path="mdiOfficeBuildingOutline" />
      </span>
      <div class="flex min-w-0 flex-1 flex-col gap-0.5">
        <p
          data-testid="doctor-invite-company"
          class="text-sm font-semibold text-gray-900 capitalize truncate"
          :title="link.company.name"
        >
          {{ link.company.name.toLowerCase() }}
        </p>
        <p
          data-testid="doctor-invite-summary"
          class="flex items-center gap-1 text-xs text-gray-600"
        >
          <SVGIcon
            :path="TONE_ICON[inviteSummary.tone].path"
            :class="TONE_ICON[inviteSummary.tone].color"
          />
          {{ inviteSummary.label }}
        </p>
      </div>
      <div v-if="inviteUrl || canResend" class="flex shrink-0 flex-wrap justify-end gap-2">
        <button
          v-if="inviteUrl"
          type="button"
          :class="ACTION_CLASS"
          data-testid="doctor-copy-invite-link-button"
          @click="copy"
        >
          <SVGIcon :path="isCopied ? mdiCheck : mdiContentCopy" class="text-sm" />
          {{ isCopied ? 'Link copiado!' : 'Copiar link do convite' }}
        </button>
        <button
          v-if="canResend"
          type="button"
          :class="ACTION_CLASS"
          :disabled="isResending"
          data-testid="doctor-resend-invite-button"
          @click="showResendConfirm = true"
        >
          <SVGIcon :path="mdiEmailSyncOutline" class="text-sm" />
          Reenviar convite
        </button>
      </div>
    </div>
  </div>

  <ConfirmActionModal
    :show="showResendConfirm"
    title="Reenviar convite"
    :description="`Deseja reenviar o convite para ${link.company.name}? Um novo convite pendente será criado.`"
    text-button="Reenviar"
    :is-loading="isResending"
    @close-modal="showResendConfirm = false"
    @handle-confirm="handleResend"
  />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  mdiCheck,
  mdiCheckCircle,
  mdiClockOutline,
  mdiCloseCircle,
  mdiContentCopy,
  mdiEmailSyncOutline,
  mdiMinusCircleOutline,
  mdiOfficeBuildingOutline
} from '@mdi/js'
import { useToast } from 'vue-toastification'

import SVGIcon from '@/components/SVGIcon.vue'
import ConfirmActionModal from '@/components/designSystem/modals/confirm.delete.action.vue'
import type { DoctorDetailCompanyLink } from '@/Network/Types/Responses/CompanyResponses'
import { CompanyDoctorInviteStatusEnum } from '@/Network/Types/Requests/CompanyRequests'
import { useDoctorDetailStore } from '@/pinia-store/useDoctorDetailStore'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { getInviteSummary } from '../../helpers/doctorStatus'
import type { InviteSummaryTone } from '../../helpers/doctorStatus'
import { getInviteErrorMessage } from '../../helpers/inviteErrors'
import { useInviteLink } from '../../helpers/useInviteLink'

const props = defineProps<{
  link: DoctorDetailCompanyLink
}>()

const toast = useToast()
const detailStore = useDoctorDetailStore()
const permissionsStore = usePermissionsStore()

// Mesmo botão compacto do "Ver arquivo" dos documentos do médico.
const ACTION_CLASS =
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-primary30 shadow-sm transition-colors hover:border-primary30 hover:bg-primary5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary30 disabled:cursor-not-allowed disabled:opacity-60'

const inviteSummary = computed(() => getInviteSummary(props.link.invite))

const TONE_ICON: Record<InviteSummaryTone, { path: string; color: string }> = {
  positive: { path: mdiCheckCircle, color: 'text-sucess' },
  warning: { path: mdiClockOutline, color: 'text-warning' },
  negative: { path: mdiCloseCircle, color: 'text-negative' },
  neutral: { path: mdiMinusCircleOutline, color: 'text-gray-400' }
}

const { inviteUrl, isCopied, copy } = useInviteLink(() => props.link.invite?.hash)

// Somente DECLINED por ora; EXPIRED/CANCELLED dependem de suporte no backend.
const RESENDABLE_DOCTOR_STATUSES = new Set<string>([CompanyDoctorInviteStatusEnum.DECLINED])

const canResend = computed(() => {
  const status = props.link.invite?.doctorStatus
  if (!status || !RESENDABLE_DOCTOR_STATUSES.has(status)) return false
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.CompanyDoctorInviteCreating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

const isResending = ref(false)
const showResendConfirm = ref(false)

async function handleResend() {
  try {
    isResending.value = true
    await detailStore.resendInvite(props.link.company.id)
    showResendConfirm.value = false
    toast.success('Convite reenviado com sucesso.')
  } catch (error) {
    console.error('Erro ao reenviar convite:', error)
    // Guard de domínio (convite já ativo → mensagem curada): exibido direto,
    // pois como fallback de notifyError a mensagem curada nunca apareceria.
    toast.error(
      getInviteErrorMessage(error, 'Não foi possível reenviar o convite. Tente novamente.')
    )
  } finally {
    isResending.value = false
  }
}
</script>
